import React, { useState, useEffect } from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useBalance, useDisconnect, usePublicClient, useWalletClient, useChainId } from 'wagmi';
import { createPublicClient, http, formatUnits, parseUnits, formatEther } from 'viem';
import { mainnet } from 'viem/chains';
import { ArrowDownCircle, ArrowUpCircle, CheckCircle, AlertCircle, Clock, Timer } from 'lucide-react';
import stakingABI from '../abi/Staking.json';

// Contract configuration
const STAKING_CONTRACT_ADDRESS = '0xEf4C1296898eD7235d31d9A8Cc242C08Ac61b397';
const BCUBE_DECIMALS = 18; // Assuming standard ERC20 decimals
const ETHEREUM_MAINNET_CHAIN_ID = 1;

const UnstakingInterface = () => {
  const { address, isConnected } = useAccount();
  const { data: balance } = useBalance({
    address: address,
  });
  const { disconnect } = useDisconnect();
  const chainId = useChainId();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  const [stakedBalance, setStakedBalance] = useState('0');
  const [availableToUnstake, setAvailableToUnstake] = useState('0');
  const [pendingUnstakes, setPendingUnstakes] = useState([]);
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Check if we're on the correct network
  const isOnCorrectNetwork = chainId === ETHEREUM_MAINNET_CHAIN_ID;

  // Fetch balances when wallet is connected
  useEffect(() => {
    if (isConnected && address) {
      if (isOnCorrectNetwork) {
        fetchBalances();
        fetchPendingUnstakes();
      } else {
        setErrorMessage(`Please switch to Ethereum Mainnet. Currently on chain ID: ${chainId}`);
        setStakedBalance('0');
        setAvailableToUnstake('0');
      }
    }
  }, [isConnected, address, chainId, isOnCorrectNetwork]);

  const fetchBalances = async () => {
    if (!publicClient || !address) return;
    
    if (!isOnCorrectNetwork) {
      setErrorMessage(`Wrong network! Please switch to Ethereum Mainnet (Chain ID: ${ETHEREUM_MAINNET_CHAIN_ID}). Current Chain ID: ${chainId}`);
      return;
    }
    
    try {
      setIsLoading(true);
      
      console.log("Fetching balance for address:", address);
      console.log("Contract address:", STAKING_CONTRACT_ADDRESS);
      console.log("Chain ID:", chainId);
      console.log("Is on correct network:", isOnCorrectNetwork);
      console.log("Using ABI:", stakingABI.abi);
      
      // Read staked balance from contract using bcubeStakeRegistry mapping
      const stakedBalanceWei = await publicClient.readContract({
        address: STAKING_CONTRACT_ADDRESS,
        abi: stakingABI.abi,
        functionName: 'bcubeStakeRegistry',
        args: [address],
      });

      console.log("stakedBalanceWei (raw):", stakedBalanceWei);
      console.log("stakedBalanceWei (type):", typeof stakedBalanceWei);
      console.log("stakedBalanceWei (toString):", stakedBalanceWei.toString());

      // Convert from wei to readable format
      const stakedBalanceFormatted = formatUnits(stakedBalanceWei, BCUBE_DECIMALS);
      console.log("stakedBalanceFormatted:", stakedBalanceFormatted);
      
      setStakedBalance(stakedBalanceFormatted);
      
      // For now, available to unstake is the same as staked balance
      // In a real implementation, this might be different based on contract logic
      setAvailableToUnstake(stakedBalanceFormatted);
      
      setErrorMessage('');
    } catch (error) {
      console.error('Error fetching balances:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      setErrorMessage(`Failed to fetch staked balance: ${error.message}`);
      setStakedBalance('0');
      setAvailableToUnstake('0');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingUnstakes = async () => {
    try {
      // For now, we'll use mock data for pending unstakes
      // In a full implementation, you'd need to track these through events or additional contract state
      await new Promise(resolve => setTimeout(resolve, 300));
      setPendingUnstakes([
        // Mock pending unstakes - replace with actual event tracking
      ]);
    } catch (error) {
      console.error('Error fetching pending unstakes:', error);
    }
  };

  const handleUnstake = async () => {
    if (!walletClient || !publicClient || !address) {
      setErrorMessage('Wallet not connected');
      return;
    }

    if (!isOnCorrectNetwork) {
      setErrorMessage(`Wrong network! Please switch to Ethereum Mainnet (Chain ID: ${ETHEREUM_MAINNET_CHAIN_ID}). Current Chain ID: ${chainId}`);
      return;
    }

    if (!unstakeAmount || parseFloat(unstakeAmount) <= 0) {
      setErrorMessage('Please enter a valid unstake amount');
      return;
    }

    if (parseFloat(unstakeAmount) > parseFloat(availableToUnstake)) {
      setErrorMessage('Insufficient staked balance');
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      // Convert amount to wei
      const unstakeAmountWei = parseUnits(unstakeAmount, BCUBE_DECIMALS);

      // Call unstake function on the contract
      const { request } = await publicClient.simulateContract({
        address: STAKING_CONTRACT_ADDRESS,
        abi: stakingABI.abi,
        functionName: 'unstake',
        args: [unstakeAmountWei],
        account: address,
      });

      const hash = await walletClient.writeContract(request);
      
      // Wait for transaction confirmation
      const receipt = await publicClient.waitForTransactionReceipt({ 
        hash,
        confirmations: 1 
      });

      if (receipt.status === 'success') {
        setSuccessMessage(`Successfully initiated unstaking of ${unstakeAmount} BCUBE tokens. Transaction: ${hash}`);
        
        // Add to pending unstakes (in real implementation, you'd track this via contract events)
        const newUnstake = {
          id: Date.now(),
          amount: unstakeAmount,
          requestedAt: Date.now(),
          unlockTime: Date.now(), // No cooldown - immediately available
          canWithdraw: true,
          txHash: hash
        };
        
        setPendingUnstakes(prev => [...prev, newUnstake]);
        setUnstakeAmount('');
        
        // Refresh balances
        setTimeout(() => {
          fetchBalances();
        }, 2000);
      } else {
        setErrorMessage('Transaction failed');
      }
    } catch (error) {
      console.error('Error unstaking:', error);
      if (error.message.includes('User rejected')) {
        setErrorMessage('Transaction was rejected');
      } else if (error.message.includes('insufficient funds')) {
        setErrorMessage('Insufficient funds for gas fees');
      } else {
        setErrorMessage(`Failed to unstake: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleWithdraw = async (unstakeId) => {
    if (!walletClient || !publicClient || !address) {
      setErrorMessage('Wallet not connected');
      return;
    }

    if (!isOnCorrectNetwork) {
      setErrorMessage(`Wrong network! Please switch to Ethereum Mainnet (Chain ID: ${ETHEREUM_MAINNET_CHAIN_ID}). Current Chain ID: ${chainId}`);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const unstakeToWithdraw = pendingUnstakes.find(u => u.id === unstakeId);
      if (!unstakeToWithdraw) {
        setErrorMessage('Unstake request not found');
        return;
      }

      if (Date.now() < unstakeToWithdraw.unlockTime) {
        setErrorMessage('Tokens not yet available for withdrawal');
        return;
      }

      // Note: The current staking contract doesn't have a separate withdraw function
      // The unstake function directly transfers tokens back to the user
      // In a more complex implementation, you might have separate unstake request and withdraw functions
      
      // For now, we'll simulate the withdrawal as the contract already transferred tokens during unstake
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setPendingUnstakes(prev => prev.filter(u => u.id !== unstakeId));
      setSuccessMessage(`Successfully completed withdrawal of ${unstakeToWithdraw.amount} BCUBE tokens!`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (error) {
      console.error('Error withdrawing:', error);
      if (error.message.includes('User rejected')) {
        setErrorMessage('Transaction was rejected');
      } else if (error.message.includes('insufficient funds')) {
        setErrorMessage('Insufficient funds for gas fees');
      } else {
        setErrorMessage(`Failed to withdraw: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMaxClick = () => {
    setUnstakeAmount(availableToUnstake);
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatTimeRemaining = (unlockTime) => {
    const now = Date.now();
    const remaining = unlockTime - now;
    
    if (remaining <= 0) return 'Ready to withdraw';
    
    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  // Auto-refresh balances every 30 seconds when connected
  useEffect(() => {
    if (!isConnected || !address) return;
    
    const interval = setInterval(() => {
      fetchBalances();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isConnected, address]);

  // Clear messages after some time
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'rgb(15, 23, 42)' }}>
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            {/* Try logo first, fallback to icon */}
            <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-lg">
              <img 
                src="/icon.png" 
                alt="BCUBE Logo" 
                className="w-12 h-12 object-contain"
                style={{ display: 'block' }}
                onLoad={(e) => {
                  console.log('✅ Logo loaded successfully');
                  e.target.parentElement.style.background = 'transparent';
                }}
                onError={(e) => {
                  console.log('❌ Logo failed to load, showing fallback');
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <ArrowDownCircle 
                className="w-8 h-8 text-indigo-400" 
                style={{ display: 'none' }}
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Unstaking Portal</h1>
          <p className="text-slate-400">Unstake your staked BCUBE tokens</p>
        </div>

        {!isConnected ? (
          /* Connect Wallet with RainbowKit */
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl p-6">
            <div className="text-center">
              <div className="w-12 h-12 text-indigo-400 mx-auto mb-4 flex items-center justify-center">
                <ArrowDownCircle className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Connect Your Wallet</h2>
              <p className="text-slate-400 mb-6">Connect your wallet to manage your BCUBE unstaking</p>
              <div className="flex justify-center">
                <ConnectButton />
              </div>
            </div>
          </div>
        ) : (
          /* Unstaking Interface */
          <div className="space-y-6">
            {/* Wallet Info */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl p-6">
              {/* <h2 className="text-xl font-bold text-white mb-6">Unstake BCUBE Tokens</h2> */}

              {/* Wallet Info */}
              <div className="flex items-center justify-between mb-6 p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-slate-300 text-sm">Connected</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-white font-mono text-sm">{formatAddress(address)}</span>
                  <ConnectButton accountStatus="avatar" showBalance={false} />
                </div>
              </div>

              {/* Balance Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="text-center p-4 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-xl border border-indigo-500/30">
                  <p className="text-slate-400 text-sm mb-1">Total Staked</p>
                  <p className="text-2xl font-bold text-white">{stakedBalance}</p>
                  <p className="text-indigo-400 text-sm">BCUBE</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                  <p className="text-slate-400 text-sm mb-1">Available to Unstake</p>
                  <p className="text-2xl font-bold text-white">{availableToUnstake}</p>
                  <p className="text-green-400 text-sm">BCUBE</p>
                </div>
              </div>

              {/* Unstake Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Amount to Unstake
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={unstakeAmount}
                      onChange={(e) => setUnstakeAmount(e.target.value)}
                      placeholder="0.0"
                      className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      step="0.01"
                      min="0"
                      max={availableToUnstake}
                    />
                    <button
                      onClick={handleMaxClick}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 px-2 py-1 rounded text-xs font-medium transition-colors"
                    >
                      MAX
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleUnstake}
                  disabled={isLoading || !unstakeAmount || parseFloat(unstakeAmount) <= 0}
                  className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <ArrowDownCircle className="w-5 h-5" />
                  )}
                  {isLoading ? 'Processing...' : 'Unstake'}
                </button>
              </div>
            </div>

            {/* Pending Unstakes */}
            {pendingUnstakes.length > 0 && (
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Timer className="w-5 h-5 text-indigo-400" />
                  Pending Unstakes
                </h3>
                <div className="space-y-3">
                  {pendingUnstakes.map((unstake) => (
                    <div key={unstake.id} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-semibold">{unstake.amount} BCUBE</p>
                          <p className="text-slate-400 text-sm">
                            {unstake.status === 'ready' ? (
                              <span className="text-green-400 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" />
                                Ready to withdraw
                              </span>
                            ) : (
                              formatTimeRemaining(unstake.unlockTime)
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => handleWithdraw(unstake.id)}
                          disabled={unstake.status !== 'ready' || isLoading}
                          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                            unstake.status === 'ready'
                              ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white'
                              : 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {unstake.status === 'ready' ? 'Withdraw' : 'Locked'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {successMessage && (
              <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-green-100 text-sm">{successMessage}</p>
              </div>
            )}

            {errorMessage && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-100 text-sm">{errorMessage}</p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-slate-500 text-xs">
            2022 - 2025. All Rights Reserved. b-cube.ai s.r.l
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnstakingInterface;
